import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Plus, MessageCircle, Mail, User, Package } from 'lucide-react';

function ProductDetailModal({ product, onClose, onAddToCart }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const images = product.images && product.images.length > 0 
    ? product.images 
    : [product.primary_image || '/placeholder-image.png'];

  function handlePrevImage() {
    setCurrentImageIndex(prev => 
      prev === 0 ? images.length - 1 : prev - 1
    );
  }

  function handleNextImage() {
    setCurrentImageIndex(prev => 
      prev === images.length - 1 ? 0 : prev + 1
    );
  }

  function handleWhatsApp() {
    const phone = product.seller.phone_number.replace(/\D/g, '');
    const message = encodeURIComponent(
      `Hola! Estoy interesado en el producto: ${product.title} (SKU: ${product.sku})`
    );
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  }

  function handleAddToCart() {
    onAddToCart(product, 1);
  }

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }

  return (
    <div 
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-brand-primary text-white p-3 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-bold font-montserrat">Detalles del Producto</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/20 rounded-lg transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="grid md:grid-cols-2 gap-4 p-4 overflow-y-auto flex-1">
          {/* LEFT SECTION: Product Details */}
          <div className="space-y-3">
            {/* Image Carousel */}
            <div className="relative aspect-square bg-slate-100 rounded-lg overflow-hidden group max-h-80">
              <img
                src={images[currentImageIndex]}
                alt={product.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = '/placeholder-image.png';
                }}
              />

              {/* Carousel Controls */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={handlePrevImage}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/95 hover:bg-white p-1.5 rounded-full shadow-lg transition-all"
                    aria-label="Imagen anterior"
                  >
                    <ChevronLeft className="w-4 h-4 text-slate-700" />
                  </button>

                  <button
                    onClick={handleNextImage}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/95 hover:bg-white p-1.5 rounded-full shadow-lg transition-all"
                    aria-label="Siguiente imagen"
                  >
                    <ChevronRight className="w-4 h-4 text-slate-700" />
                  </button>

                  {/* Image Counter */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white px-2.5 py-0.5 rounded-full text-xs font-montserrat">
                    {currentImageIndex + 1} / {images.length}
                  </div>
                </>
              )}
            </div>

            {/* Thumbnail Gallery */}
            {images.length > 1 && (
              <div className="grid grid-cols-5 gap-1.5">
                {images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`aspect-square rounded overflow-hidden border-2 transition-all ${
                      index === currentImageIndex
                        ? 'border-brand-primary ring-2 ring-brand-primary/20'
                        : 'border-slate-200 hover:border-brand-primary/50'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${product.title} - ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = '/placeholder-image.png';
                      }}
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Product Information */}
            <div className="space-y-3">
              <div>
                <h3 className="text-xl font-bold text-slate-800 font-montserrat mb-1">
                  {product.title}
                </h3>
                <div className="flex items-center gap-2 text-xs text-slate-500 font-montserrat">
                  <Package className="w-3.5 h-3.5" />
                  <span>SKU: {product.sku}</span>
                </div>
              </div>

              {product.description && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-700 font-montserrat mb-1">
                    Descripción
                  </h4>
                  <p className="text-xs text-slate-600 font-montserrat leading-relaxed whitespace-pre-line max-h-24 overflow-y-auto">
                    {product.description}
                  </p>
                </div>
              )}

              {/* Pricing Section */}
              <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                <h4 className="text-xs font-semibold text-slate-700 font-montserrat">
                  Precios Disponibles
                </h4>
                
                {product.bulk_price > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-600 font-montserrat">Precio Mayorista:</span>
                    <span className="text-base font-bold text-brand-gold font-montserrat">
                      S/ {product.bulk_price.toFixed(2)}
                    </span>
                  </div>
                )}
                
                {product.dropship_price > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-600 font-montserrat">Precio Dropship:</span>
                    <span className="text-base font-bold text-brand-green font-montserrat">
                      S/ {product.dropship_price.toFixed(2)}
                    </span>
                  </div>
                )}

                {product.price > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-600 font-montserrat">Precio Regular:</span>
                    <span className="text-base font-bold text-brand-primary font-montserrat">
                      S/ {product.price.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              {/* Add to Cart Button */}
              <button
                onClick={handleAddToCart}
                className="w-full flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary/90 text-white py-2.5 rounded-lg transition-all font-montserrat font-semibold text-sm shadow-md hover:shadow-lg"
              >
                <Plus className="w-4 h-4" />
                Agregar al Carrito
              </button>
            </div>
          </div>

          {/* RIGHT SECTION: Provider Information */}
          <div className="space-y-3">
            <div className="bg-gradient-to-br from-brand-primary to-brand-primary/80 rounded-lg p-4 text-white">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs opacity-90 font-montserrat">Proveedor</p>
                  <h3 className="text-lg font-bold font-montserrat truncate">
                    {product.seller.username}
                  </h3>
                </div>
              </div>

              <div className="space-y-2">
                {/* Email */}
                {product.seller.email && (
                  <div className="flex items-start gap-2 bg-white/10 rounded-lg p-2">
                    <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] opacity-75 font-montserrat mb-0.5">Email</p>
                      <a 
                        href={`mailto:${product.seller.email}`}
                        className="text-xs font-semibold hover:underline font-montserrat break-all"
                      >
                        {product.seller.email}
                      </a>
                    </div>
                  </div>
                )}

                {/* WhatsApp */}
                <div className="flex items-start gap-2 bg-white/10 rounded-lg p-2">
                  <MessageCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-[10px] opacity-75 font-montserrat mb-0.5">WhatsApp</p>
                    <p className="text-xs font-semibold font-montserrat">
                      {product.seller.phone_number}
                    </p>
                  </div>
                </div>
              </div>

              {/* WhatsApp Button */}
              <button
                onClick={handleWhatsApp}
                className="w-full mt-3 flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white py-2.5 rounded-lg transition-all font-montserrat font-semibold text-sm shadow-md hover:shadow-lg"
              >
                <MessageCircle className="w-4 h-4" />
                Contactar por WhatsApp
              </button>
            </div>

            {/* Additional Provider Info */}
            <div className="bg-slate-50 rounded-lg p-3 space-y-2">
              <h4 className="text-sm font-bold text-slate-800 font-montserrat">
                Información del Proveedor
              </h4>
              
              <div className="space-y-1.5 text-xs text-slate-600 font-montserrat">
                <div className="flex items-start gap-1.5">
                  <div className="w-1.5 h-1.5 bg-brand-primary rounded-full mt-1 flex-shrink-0" />
                  <p>Proveedor verificado de FAST MAX</p>
                </div>
                <div className="flex items-start gap-1.5">
                  <div className="w-1.5 h-1.5 bg-brand-primary rounded-full mt-1 flex-shrink-0" />
                  <p>Envíos disponibles a Lima y Provincia</p>
                </div>
                <div className="flex items-start gap-1.5">
                  <div className="w-1.5 h-1.5 bg-brand-primary rounded-full mt-1 flex-shrink-0" />
                  <p>Soporte directo por WhatsApp</p>
                </div>
              </div>

              <div className="pt-2 border-t border-slate-200">
                <p className="text-[10px] text-slate-500 font-montserrat leading-relaxed">
                  Al agregar este producto al carrito, podrás continuar con el proceso de envío usando el sistema FAST MAX.
                </p>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-white border-2 border-slate-200 rounded-lg p-2 text-center">
                <div className="text-xl mb-0.5">🚚</div>
                <p className="text-[10px] font-semibold text-slate-700 font-montserrat leading-tight">Envío Seguro</p>
              </div>
              <div className="bg-white border-2 border-slate-200 rounded-lg p-2 text-center">
                <div className="text-xl mb-0.5">✓</div>
                <p className="text-[10px] font-semibold text-slate-700 font-montserrat leading-tight">Verificado</p>
              </div>
              <div className="bg-white border-2 border-slate-200 rounded-lg p-2 text-center">
                <div className="text-xl mb-0.5">💬</div>
                <p className="text-[10px] font-semibold text-slate-700 font-montserrat leading-tight">Soporte</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetailModal;